import React, {useEffect, useRef, useState} from 'react';
import type {Note} from '../types/note';
import type {Attachment} from '../types/attachment';
import {addAttachment, deleteAttachment} from '../api';
import { useCreateNote, useUpdateNote, useDeleteNote } from '../hooks/useNoteMutations';
import './NoteForm.css';
import './Toolbar.css';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {BACKEND_URL} from '../config';
import {NoteEditor} from './NoteEditor';
import ConfirmModal from './ConfirmModal';


export default function NoteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditing = !!id;
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const linkUrlRef = useRef<HTMLInputElement>(null);
  const linkTextRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [noteId, setNoteId] = useState<number | null>(id ? parseInt(id) : null);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editor, setEditor] = useState<any>(null);

  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();

  const [currentNote, setCurrentNote] = useState<Note>(location.state?.note);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(
    location.state?.selectedCategoryId || location.state?.category_id || location.state?.note?.category_id || 0
  );

  // Update form state when note data is loaded
  useEffect(() => {
    if (currentNote) {
      setCurrentNote(currentNote);
      setAttachments(currentNote.attachments || []);
      setSelectedCategoryId(currentNote.category_id);
    }
  }, [currentNote]);

  const handleEditorReady = (editorInstance: any) => {
    setEditor(editorInstance);
  };

  const handleSave = async (content: string) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Upload any pending images first
      const uploadPromises = pendingImages.map(file => handleUploadAttachment(file));
      await Promise.all(uploadPromises);

      const noteData = {
        content,
        category_id: selectedCategoryId
      };

      let savedNote: Note;
      if (location.state?.note) {
        savedNote = await updateNoteMutation.mutateAsync({ 
          id: location.state.note.id, 
          note: noteData 
        });
      } else {
        savedNote = await createNoteMutation.mutateAsync(noteData);
      }

      // Upload any pending attachments
      if (pendingImages.length > 0) {
        await Promise.all(
          pendingImages.map(file =>
            addAttachment(savedNote.id, file)
          )
        );
      }

      navigate(`/notes/${savedNote.id}/view`);
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadAttachment = async (file: File) => {
    try {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      const attachment = await addAttachment(location.state?.note?.id || '', file, (event) => {
        if (Number.isFinite(event.total) && event.total! > 0) {
          setUploadProgress(prev => ({ ...prev, [file.name]: Math.round((event.loaded / event.total!) * 100) }));
        }
      });
      setAttachments(prev => [...prev, attachment]);
      return attachment;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      throw error;
    }
  };

  const handleCancel = async () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    // If this is a new note and we created a temporary one for attachments, delete it
    if (!isEditing && noteId) {
      try {
        await deleteNoteMutation.mutateAsync(noteId);
      } catch (error) {
        console.error('Error deleting temporary note:', error);
      }
    }
    // Navigate to note view if editing, otherwise to notes list
    if (id) {
      navigate(`/notes/${id}/view`);
    } else {
      navigate('/notes');
    }
  };

  const handleCancelModal = () => {
    setShowConfirm(false);
  };

  const handleAddImage = async (file: File) => {
    try {
      console.log('[NoteForm] handleAddImage called with file:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      let currentNoteId = noteId;
      if (!currentNoteId) {
        const tempNote = await createNoteMutation.mutateAsync({ content: '', category_id: selectedCategoryId });
        currentNoteId = tempNote.id;
        setNoteId(tempNote.id);
      }
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
      const attachment = await addAttachment(currentNoteId, file, (event) => {
        if (Number.isFinite(event.total) && event.total! > 0) {
          setUploadProgress(prev => ({ ...prev, [file.name]: Math.round((event.loaded / event.total!) * 100) }));
        }
      });
      setAttachments(prev => [...prev, attachment]);
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      return `${BACKEND_URL}/${attachment.file_path}`;
    } catch (error) {
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      console.error('[NoteForm] Error adding image:', error);
      return null;
    }
  };

  const handleAddFile = async (file: File) => {
    try {
      if (noteId) {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        const attachment = await addAttachment(noteId, file, (event) => {
          if (Number.isFinite(event.total) && event.total! > 0) {
            setUploadProgress(prev => ({ ...prev, [file.name]: Math.round((event.loaded / event.total!) * 100) }));
          }
        });
        setAttachments(prev => [...prev, attachment]);
        setUploadProgress(prev => {
          const { [file.name]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setPendingImages(prev => [...prev, file]);
      }
    } catch (error) {
      setUploadProgress(prev => {
        const { [file.name]: _, ...rest } = prev;
        return rest;
      });
      console.error('Error adding file:', error);
    }
  };

  const handleSetLink = () => {
    setShowLinkDialog(true);
    // The actual link insertion is handled by NoteEditor
  };

  const handleRemoveAttachment = async (attachmentId: number) => {
    try {
      const attachment = attachments.find(a => a.id === attachmentId);
      if (!attachment) return;

      // Delete the attachment from the server
      await deleteAttachment(attachmentId);

      // Remove from attachments state
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));

      // If this is an image and we have an editor instance, remove it from the content
      if (attachment.mime_type.startsWith('image/') && editor) {
        const content = editor.getHTML();
        if (content.includes(`${BACKEND_URL}/${attachment.file_path}`)) {
          // Find and remove the image node
          editor.state.doc.descendants((node: any, pos: number) => {
            if (node.type.name === 'image' && node.attrs.src === `${BACKEND_URL}/${attachment.file_path}`) {
              editor.chain().focus().deleteRange({ from: pos, to: pos + 1 }).run();
              return false;
            }
          });
        }
      }
    } catch (error) {
      console.error('Error removing attachment:', error);
    }
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingImages(prev => {
      const file = prev[index];
      // Remove upload progress for this file
      setUploadProgress(progress => {
        const { [file.name]: _, ...rest } = progress;
        return rest;
      });
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <div className="note-form">
      <NoteEditor
        note={currentNote}
        isSaving={isSaving}
        onSave={handleSave}
        onAddImage={handleAddImage}
        onAddFile={handleAddFile}
        onSetLink={handleSetLink}
        onEditorReady={handleEditorReady}
        showSaveButton={true}
        onCancel={handleCancel}
        uploadProgress={uploadProgress}
        selectedCategoryId={selectedCategoryId}
        categoryBreadcrumb={location.state?.categoryBreadcrumb}
        onCategoryChange={setSelectedCategoryId}
      />

      {(attachments.length > 0 || pendingImages.length > 0) && (
        <div className="attachments">
          <h3>Attachments</h3>
          <div className="attachments-grid">
            {/* Show current attachments */}
            {attachments.map(attachment => (
              <div key={attachment.id} className="attachment-item relative group">
                <button
                  onClick={() => handleRemoveAttachment(attachment.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                  title="Remove attachment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {attachment.mime_type.startsWith('image/') ? (
                  <img 
                    src={`${BACKEND_URL}/${attachment.file_path}`} 
                    alt={attachment.filename}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                ) : (
                  <a 
                    href={`${BACKEND_URL}/${attachment.file_path}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 truncate">{attachment.filename}</span>
                    </div>
                  </a>
                )}
              </div>
            ))}

            {/* Show pending attachments */}
            {pendingImages.map((file, index) => (
              <div key={`pending-${index}`} className="attachment-item relative group">
                <button
                  onClick={() => handleRemovePendingFile(index)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                  title="Remove attachment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {file.type.startsWith('image/') ? (
                  <div className="relative">
                    <img 
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-32 object-cover rounded-lg opacity-50"
                    />
                    <div className="flex flex-col h-full">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-white text-sm">Pending upload</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="block p-4 bg-gray-50 rounded-lg opacity-50">
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">Pending upload</span>
                  </div>
                )}
                {uploadProgress[file.name] !== undefined && (
                  <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-200 rounded-b-lg flex items-center">
                    <div
                      className="h-2 bg-teal-500 rounded-b-lg transition-all duration-200"
                      style={{ width: `${uploadProgress[file.name]}%` }}
                    />
                    <span className="ml-2 text-xs text-gray-700" style={{ minWidth: '2.5rem' }}>{uploadProgress[file.name]}%</span>
                  </div>
                )}
              </div>
            ))}

            {/* Show progress for files being uploaded directly (not in pendingImages) */}
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              !attachments.some(att => att.filename === fileName) && !pendingImages.some(file => file.name === fileName) && (
                <div key={`uploading-${fileName}`} className="attachment-item relative group">
                  <div className="block p-4 bg-gray-50 rounded-lg opacity-50">
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 truncate">{fileName}</span>
                    </div>
                    <span className="text-xs text-gray-500">Uploading...</span>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-200 rounded-b-lg flex items-center">
                    <div
                      className="h-2 bg-teal-500 rounded-b-lg transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                    <span className="ml-2 text-xs text-gray-700" style={{ minWidth: '2.5rem' }}>{progress}%</span>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {id && currentNote && (
        <div className="note-meta mt-8 text-right text-sm text-gray-500">
          <span className="note-date block">Created: {new Date(currentNote.created_at).toLocaleString()}</span>
          <span className="note-date block">Last updated: {new Date(currentNote.updated_at).toLocaleString()}</span>
        </div>
      )}

      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <form className="bg-white p-4 rounded-lg shadow-lg w-96">
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link Text
              </label>
              <input
                ref={linkTextRef}
                type="text"
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter link text"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link URL
              </label>
              <input
                ref={linkUrlRef}
                type="url"
                className="w-full px-3 py-2 border rounded"
                placeholder="Enter link URL"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowLinkDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmModal
        open={showConfirm}
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onConfirm={handleConfirm}
        onCancel={handleCancelModal}
        confirmText="Yes"
        cancelText="Cancel"
      />
    </div>
  );
} 